import { ColGroupDef, ColDef, GridOptions } from 'ag-grid-community'
import { ProductDetail } from 'api'
import {
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  TypedValueGetterParams,
  gridOptionsBase
} from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { MarketHelper } from 'model'
import { ProductActionsButton } from './ProductActionsButton'
import { getTradingStatus, startTimeZoneFormatter } from 'view/shared/datagrid'

export enum ProductGridColumnId {
  PRODUCT_ACTIONS = 'PRODUCT_ACTIONS',
  PRODUCT_NAME = 'PRODUCT_NAME',
  PRODUCT_MARKET = 'PRODUCT_MARKET',
  PRODUCT_MIC_CODE = 'PRODUCT_MIC_CODE',
  PRODUCT_START_TIME = 'PRODUCT_START_TIME',
  PRODUCT_START_TIME_ZONE = 'PRODUCT_START_TIME_ZONE',
  PRODUCT_STATUS = 'PRODUCT_STATUS',
  PRODUCT_SECONDARY_MARKET = 'PRODUCT_SECONDARY_MARKET',
  PRODUCT_SECONDARY_MIC_CODE = 'PRODUCT_SECONDARY_MIC_CODE',
  PRODUCT_SECONDART_START_TIME = 'PRODUCT_SECONDART_START_TIME',
  PRODUCT_SECONDARY_START_TIME_ZONE = 'PRODUCT_SECONDARY_START_TIME_ZONE',
  PRODUCT_SECONDARY_STATUS = 'PRODUCT_SECONDARY_STATUS'
}

export const ProductColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: ProductGridColumnId.PRODUCT_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 180,
    minWidth: 180,
    headerComponent: withAllContext(createGroupedActionRenderer(ProductActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(ProductActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: ProductGridColumnId.PRODUCT_NAME,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.name,
    headerName: Locale.translate(Translation.TC_PRODUCT_NAME),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_MARKET,
    headerName: Locale.translate(Translation.TC_PRIMARY_MARKET),
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => {
      const market = params.data?.product.market
      return market && MarketHelper.getName(market)
    },
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_MIC_CODE,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.micCode,
    headerName: Locale.translate(Translation.TC_PRIMARY_MIC),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_START_TIME,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.startTime,
    headerName: Locale.translate(Translation.TC_PRIMARY_START_TIME),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_START_TIME_ZONE,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.startTimeZone,
    valueFormatter: startTimeZoneFormatter,
    headerName: Locale.translate(Translation.TC_PRIMARY_TIME_ZONE),
    width: 180,
    filter: 'agSetColumnFilter'
  },

  {
    colId: ProductGridColumnId.PRODUCT_STATUS,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => Locale.translate(getTradingStatus(params.data?.product.tradingStatus)),
    headerName: Locale.translate(Translation.TC_PRIMARY_TRADING_STATUS),
    width: 180,
    filter: 'agSetColumnFilter'
  },

  {
    colId: ProductGridColumnId.PRODUCT_SECONDARY_MARKET,
    headerName: Locale.translate(Translation.TC_SECONDARY_MARKET),
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => {
      const market = params.data?.product.secondaryMarket
      return market && MarketHelper.getName(market)
    },
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_SECONDARY_MIC_CODE,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.secondaryMicCode,
    headerName: Locale.translate(Translation.TC_SECONDARY_MIC),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_SECONDART_START_TIME,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.secondaryStartTime,
    headerName: Locale.translate(Translation.TC_SECONDARY_START_TIME),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ProductGridColumnId.PRODUCT_SECONDARY_START_TIME_ZONE,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) => params.data?.product.secondaryStartTimeZone,
    valueFormatter: startTimeZoneFormatter,
    headerName: Locale.translate(Translation.TC_SECONDARY_TIME_ZONE),
    width: 180,
    filter: 'agSetColumnFilter'
  },

  {
    colId: ProductGridColumnId.PRODUCT_SECONDARY_STATUS,
    valueGetter: (params: TypedValueGetterParams<ProductDetail>) =>
      Locale.translate(getTradingStatus(params.data?.product.secondaryTradingStatus)),
    headerName: Locale.translate(Translation.TC_SECONDARY_TRADING_STATUS),
    width: 180,
    filter: 'agSetColumnFilter'
  }
]

export const productGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.product.id
}
